import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JenisPortofolio, Periode, SysProgramStudi, Unitkerja } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox"
import { useSearchParams } from "react-router-dom"
import { getListUnitkerja } from "@/services/getData"
import SkeletonRow from "@/components/SkeletonRow"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { listJenisPortofolio } from "@/constants"

type FormSysProdi = {
  id_sys_periode: string
  list_prodi: SysProgramStudi[]
}

const postData = async (newData: FormSysProdi) => {
  const response = await api.post("/setting/sys-prodi/store-multiple", newData)
  return response.data
}

const patchData = async (newData: FormSysProdi) => {
  const response = await api.patch(
    "/setting/sys-prodi/update-multiple",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: { id_sys_periode: string }) => {
  const response = await api({
    url: "/setting/sys-prodi/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function FormSysProdi({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Periode | null
  closeModal: () => void
}) {
  const [searchParams] = useSearchParams()
  const id_sys_periode_params = searchParams.get("id")
  const [searchQuery, setSearchQuery] = useState("")

  const { data: dataUnitkerja, isPending: isPendingUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  })
  const dataProdi = dataUnitkerja?.filter((unitkerja: Unitkerja) => {
    const isValidUnitkerja =
      unitkerja?.unitkerja_group_key == "5" && unitkerja?.is_active

    const isMatchingJenjang = selectedRow?.jenjang_buka?.some((jenjang) => {
      return jenjang.nama_jenjang_pendidikan === unitkerja.uk_program
    })

    return isValidUnitkerja && isMatchingJenjang
  })
  const filteredListProdi = dataProdi?.filter((prodi: Unitkerja) => {
    return prodi.nama?.toLowerCase()?.includes(searchQuery.toLowerCase())
  })

  const [form, setForm] = useState<FormSysProdi>({
    id_sys_periode: selectedRow?.id_sys_periode || id_sys_periode_params || "",
    list_prodi: selectedRow?.sys_prodi?.map((prodi) => prodi) || [],
  })
  const [isCheckAll, setIsCheckAll] = useState(false)

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPeriode"] })
      queryClient.invalidateQueries({
        queryKey: ["periode", id_sys_periode_params],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Setting Program Studi"
          description={`Apakah anda yakin akan menghapus data?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data List Program Studi"
            : "Update Data List Program Studi"
        }
      />
      {isPendingUnitkerja ? (
        <div className="my-5 grid grid-cols-6 gap-5">
          {Array(30)
            .fill(null)
            .map((_, index) => {
              return <SkeletonRow key={index} />
            })}
        </div>
      ) : (
        <>
          {isPost && (
            <div className="flex flex-col gap-y-3">
              <Input
                placeholder="Search prodi..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <div className="grid grid-cols-3 gap-3">
                {filteredListProdi &&
                  filteredListProdi.length > 0 &&
                  filteredListProdi.map((prodi: Unitkerja) => {
                    const isChecked = form.list_prodi?.some(
                      (p) => p.uk_id === prodi.uk_id
                    )

                    return (
                      <div key={prodi.uk_id} className="flex gap-3">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => {
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: isChecked
                                ? prev.list_prodi.filter(
                                    (p) => p.uk_id !== prodi.uk_id
                                  )
                                : [
                                    ...prev.list_prodi,
                                    {
                                      id_sys_program_studi: "",
                                      id_sys_periode: form.id_sys_periode,
                                      uk_id: prodi.uk_id,
                                      is_kelas_denpasar: false,
                                      is_cek_butawarna: false,
                                      daya_tampung: 0,
                                    },
                                  ],
                            }))
                          }}
                        />
                        <Label>{prodi.nama}</Label>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {isPatch && (
            <div className="flex flex-col gap-y-3">
              <Input
                placeholder="Search prodi..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />

              {filteredListProdi &&
                filteredListProdi.length > 0 &&
                filteredListProdi.map((prodi: Unitkerja) => {
                  const existingProdi = form.list_prodi.find(
                    (p) => p.uk_id === prodi.uk_id
                  )
                  const isChecked = !!existingProdi

                  return (
                    <div
                      key={prodi.uk_id}
                      className="grid grid-cols-6 gap-5 border-b"
                    >
                      <div className="mt-5 flex gap-3">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => {
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: isChecked
                                ? prev.list_prodi.filter(
                                    (p) => p.uk_id !== prodi.uk_id
                                  )
                                : [
                                    ...prev.list_prodi,
                                    {
                                      id_sys_program_studi: "",
                                      id_sys_periode: form.id_sys_periode,
                                      uk_id: prodi.uk_id,
                                      is_kelas_denpasar: false,
                                      daya_tampung: 0,
                                    },
                                  ],
                            }))
                          }}
                        />
                        <Label>{prodi.nama}</Label>
                      </div>

                      <div>
                        <Label>Daya Tampung</Label>
                        <Input
                          required
                          type="number"
                          placeholder="Ketik daya tampung..."
                          value={existingProdi?.daya_tampung || 0}
                          onChange={(event) => {
                            const updatedDayaTampung = Number(
                              event.target.value
                            )
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: prev.list_prodi.map((p) =>
                                p.uk_id === prodi.uk_id
                                  ? { ...p, daya_tampung: updatedDayaTampung }
                                  : p
                              ),
                            }))
                          }}
                        />
                      </div>

                      <div>
                        <Label>Tahun Kurikulum</Label>
                        <Input
                          maxLength={4}
                          placeholder="Ketik tahun kurikulum..."
                          value={existingProdi?.tahun_kurikulum}
                          onChange={(event) => {
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: prev.list_prodi.map((p) =>
                                p.uk_id === prodi.uk_id
                                  ? {
                                      ...p,
                                      tahun_kurikulum: event.target.value,
                                    }
                                  : p
                              ),
                            }))
                          }}
                        />
                      </div>

                      {/* <div className="mt-5 flex gap-3">
                        <Checkbox
                          id={`is_kelas_denpasar_${prodi.uk_id}`}
                          checked={existingProdi?.is_kelas_denpasar || false}
                          onCheckedChange={(value) => {
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: prev.list_prodi.map((p) =>
                                p.uk_id === prodi.uk_id
                                  ? { ...p, is_kelas_denpasar: Boolean(value) }
                                  : p
                              ),
                            }));
                          }}
                        />
                        <Label htmlFor={`is_kelas_denpasar_${prodi.uk_id}`}>
                          Kelas Denpasar
                        </Label>
                      </div> */}

                      <div className="mt-5 flex gap-3">
                        <Checkbox
                          id={`is_portofolio${prodi.uk_id}`}
                          checked={existingProdi?.is_portofolio || false}
                          onCheckedChange={(value) => {
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: prev.list_prodi.map((p) =>
                                p.uk_id === prodi.uk_id
                                  ? { ...p, is_portofolio: Boolean(value) }
                                  : p
                              ),
                            }))
                          }}
                        />
                        <Label htmlFor={`is_portofolio${prodi.uk_id}`}>
                          Wajib Portofolio
                        </Label>
                      </div>

                      <div>
                        <Label required>Jenis Portofolio</Label>
                        <Select
                          value={existingProdi?.jenis_portofolio || undefined}
                          onValueChange={(value: string) => {
                            const jenisPortofolio = value as JenisPortofolio

                            setForm((prev) => ({
                              ...prev,
                              list_prodi: prev.list_prodi.map((p) =>
                                p.uk_id === prodi.uk_id
                                  ? { ...p, jenis_portofolio: jenisPortofolio }
                                  : p
                              ),
                            }))
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={"Pilih Jenis Portofolio"}>
                              {existingProdi?.jenis_portofolio &&
                                listJenisPortofolio?.find(
                                  ({ value }) =>
                                    value === existingProdi?.jenis_portofolio
                                )?.label}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {listJenisPortofolio?.map(
                              ({ label, value }, index) => (
                                <SelectItem key={index} value={`${value}`}>
                                  {label}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mt-5 flex gap-3">
                        <Checkbox
                          id={`is_cek_butawarna${prodi.uk_id}`}
                          checked={existingProdi?.is_cek_butawarna || false}
                          onCheckedChange={(value) => {
                            setForm((prev) => ({
                              ...prev,
                              list_prodi: prev.list_prodi.map((p) =>
                                p.uk_id === prodi.uk_id
                                  ? { ...p, is_cek_butawarna: Boolean(value) }
                                  : p
                              ),
                            }))
                          }}
                        />
                        <Label htmlFor={`is_cek_butawarna${prodi.uk_id}`}>
                          Cek Buta Warna
                        </Label>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </>
      )}

      {isPost && (
        <div className="flex gap-3">
          <Checkbox
            checked={isCheckAll}
            onCheckedChange={() => {
              setIsCheckAll((prev) => !prev)

              setForm((prev) => ({
                ...prev,
                list_prodi: isCheckAll
                  ? []
                  : dataProdi?.map((prodi: Unitkerja) => ({
                      id_sys_program_studi: "",
                      id_sys_periode: prev.id_sys_periode,
                      uk_id: prodi.uk_id,
                      is_kelas_denpasar: false,
                      daya_tampung: 0,
                    })) || [],
              }))
            }}
          />

          <Label className="text-primary">Check All</Label>
        </div>
      )}

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
